import { featuresFromModuleToggles } from "@/domain/erpFeatures";
import { permissionsForCustomerRole } from "@/domain/customerPermissions";
import { PERMISSION_FEATURE_MAP } from "@/domain/permissionCatalog";
import { PLATFORM_ROLES } from "@/domain/platformPermissions";
import { usePlatformStore } from "@/store/platform.store";
import { useTenantUsersStore } from "@/store/tenantUsers.store";

const DEV_PASSWORD = "Gps@1234";
const SHOW_DEV_USERS =
  String(import.meta.env.VITE_SHOW_DEV_USERS ?? "true") !== "false";

const SESSION_TTL_MS = 12 * 60 * 60 * 1000;

function resolveCustomerPermissions(role, modules) {
  const grants = permissionsForCustomerRole(role);
  const enabledFeatures = new Set(featuresFromModuleToggles(modules));
  return grants.filter((perm) => {
    const feature = PERMISSION_FEATURE_MAP[perm];
    if (!feature) return true;
    return enabledFeatures.has(feature);
  });
}

function authenticateCustomer(email, password) {
  const trimmed = email.trim().toLowerCase();
  if (password !== DEV_PASSWORD) {
    return { ok: false, error: "invalid_credentials" };
  }
  const tenant = useTenantUsersStore.getState().getUserByEmail(trimmed);
  if (!tenant || !tenant.active) {
    return { ok: false, error: "unknown_user" };
  }
  const customer = usePlatformStore
    .getState()
    .getCustomer(tenant.systemCustomerId ?? "sc-dev-gps");
  if (!customer || customer.status === "suspended") {
    return { ok: false, error: "customer_suspended" };
  }
  const modules = customer.modules ?? {};
  const permissions = resolveCustomerPermissions(tenant.role, modules);
  const features = featuresFromModuleToggles(modules);
  return {
    ok: true,
    user: {
      id: tenant.id,
      name: tenant.name,
      email: tenant.email,
      role: tenant.role,
      systemCustomerId: tenant.systemCustomerId ?? "sc-dev-gps",
      companyIds: tenant.companyIds,
      permissions,
      features,
    },
    modules,
    expiresAt: Date.now() + SESSION_TTL_MS,
  };
}

function authenticatePlatform(email, password) {
  const trimmed = email.trim().toLowerCase();
  if (password !== DEV_PASSWORD) {
    return { ok: false, error: "invalid_credentials" };
  }
  const op = usePlatformStore.getState().getOperatorByEmail(trimmed);
  if (!op || !op.active) {
    return { ok: false, error: "unknown_user" };
  }
  const role = PLATFORM_ROLES.find((r) => r.id === op.roleId);
  return {
    ok: true,
    operator: {
      ...op,
      permissions: role?.permissions ?? [],
    },
    expiresAt: Date.now() + SESSION_TTL_MS,
  };
}

function isSessionExpired(expiresAt) {
  if (!expiresAt) return false;
  return Date.now() > expiresAt;
}

export {
  DEV_PASSWORD,
  SHOW_DEV_USERS,
  SESSION_TTL_MS,
  authenticateCustomer,
  authenticatePlatform,
  isSessionExpired,
  resolveCustomerPermissions,
};
